/**
 * Housing Training — /housing/training
 * Role-aware, project-specific training for housing project users.
 * 5 housing modules, progress tracking, certificate download.
 */
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, PlayCircle, ChevronRight, Download, Award, RefreshCw } from "lucide-react";
import { toast } from "sonner";

import { PageHeader, SectionCard, MetricCard, CTAButton } from "@/components/ds";
const MODULES = [
  {
    id: "lc-basics", order: 1,
    title: "Low-Carbon Housing Basics", duration: "6 min",
    why:       "Construction accounts for 11% of global energy-related CO₂ emissions. Embodied carbon from materials can exceed 50 years of operational carbon. Yet most frameworks still focus on energy efficiency during use.",
    concept:   "Low-carbon housing reduces emissions at construction — not just in use. Materials (cement, steel, masonry) are where the biggest reductions are possible at scale.",
    project:   "For Coop Green Homes, cement is 58% of baseline embodied carbon. SCM substitution and recycled steel reduce embodied from 8.28 to 6.64 tCO₂e/unit — 20% reduction, one-time and permanent.",
    practical: "Before selecting a supplier, request their Environmental Product Declaration (EPD). Suppliers who cannot provide EPDs signal poor traceability and low material quality.",
    checklist: ["Distinguish embodied from operational carbon", "Know the 3 material drivers (cement, steel, masonry)", "Identify your project's dominant source on Climate Snapshot"],
    red_flag:  "Red flag: A project claiming 'net zero' based only on solar panels, without addressing embodied carbon in construction, is making an incomplete and misleading claim.",
  },
  {
    id: "embodied-vs-op", order: 2,
    title: "Embodied vs Operational Carbon", duration: "7 min",
    why:       "The biggest accounting mistake in housing is treating all emissions as equivalent. Embodied emissions are locked in at construction — permanent and one-time. Operational emissions recur annually and can be reduced through retrofits. The strategies are completely different.",
    concept:   "Embodied = emitted during material production. Fixed at construction. One-time. Operational = annual energy use emissions. Recurring. Reducible over time.",
    project:   "Coop: embodied reduction 1.64 tCO₂e/unit (one-time, at build). Operational reduction 1.13 tCO₂e/yr/unit (recurring). Over 30yr, operational accumulates to 33.9t — but embodied reduction happens immediately and permanently.",
    practical: "When presenting: 'Our embodied reduction of 1.64t/unit happens once at construction. Our operational reduction of 1.13t/yr is recurring across the lifetime.' This distinction matters for credit structuring and financing.",
    checklist: ["State your embodied reduction per unit with '(one-time)'", "State your operational reduction per unit with '(per year)'", "Never add them without specifying the timeframe"],
    red_flag:  "Red flag: Documents that add embodied + total lifetime operational without a timeframe are misleading. Always state: 'X tCO₂e over Y years, including Z tCO₂e embodied.'",
  },
  {
    id: "carbon-pathways", order: 3,
    title: "Carbon Pathways for Housing Projects", duration: "8 min",
    why:       "Most housing projects qualify for at least one carbon market pathway — but the wrong choice wastes months. The right pathway depends on scale, data quality, buyer relationships, and MRV capacity.",
    concept:   "(1) Insetting: supply chain decarbonisation, no credits required. (2) VCM: voluntary market credits, requires verification. (3) PoA: programme bundling for scale. (4) Article 6: sovereign compliance, requires government authorization.",
    project:   "Coop qualifies for Insetting (High) and PoA (High). VCM is conditional — needs third-party verification. Article 6 is not eligible: requires DNA authorization not yet initiated.",
    practical: "Start with Insetting — no credits, no external verification, implementable immediately through buyer agreements. Use PoA prep time (6-12 months) to build your MRV and evidence base. Approach VCM only after the evidence vault is complete.",
    checklist: ["Know all 4 pathways and their eligibility criteria", "Understand why Insetting is the fastest pathway", "Check the Carbon Strategy page for current status"],
    red_flag:  "Red flag: Any advisor claiming Article 6 credits are possible 'immediately' for a new-build housing project without host country DNA engagement is over-promising.",
  },
  {
    id: "mrv-readiness", order: 4,
    title: "MRV and Documentation Readiness", duration: "8 min",
    why:       "MRV converts your climate project from a claim into a credible, verifiable, financeable asset. Without a credible MRV system, no bank, verifier, or carbon registry will engage seriously with your project.",
    concept:   "Monitoring (collecting data) + Reporting (organising data) + Verification (independent review). Each step builds on the previous. Without monitoring, reporting is guesswork. Without verification, credits are not issued.",
    project:   "Coop currently has Medium confidence — inputs are self-reported. To reach High (required for formal verification): supplier certificates for cement/steel, utility bills or smart meters, solar inverter logs. All are in the Evidence Vault as required items.",
    practical: "Treat MRV as infrastructure, not paperwork. Collect supplier certificates at every delivery. Install smart meters from day one. This data is worth more than it costs — it is the foundation of your carbon asset.",
    checklist: ["Know what MRV stands for and why each part matters", "Identify 2 missing items in your Evidence Vault", "Understand the difference between Medium and High confidence"],
    red_flag:  "Red flag: Retrospective evidence collection (after the fact) is always weaker than contemporaneous records. Never let supplier certificates accumulate — collect them at delivery.",
  },
  {
    id: "fi-and-verifiers", order: 5,
    title: "Working with Financial Institutions and Verifiers", duration: "7 min",
    why:       "Banks and verifiers do not have the same needs. Confusing them is one of the most common errors in climate project finance. Sending the wrong document to the wrong audience wastes both parties' time and signals poor preparation.",
    concept:   "Finance Brief = decision support for banks (financial metrics, risk, investment logic). Climate Credentials Report = technical evidence for verifiers and ESG teams (methodology, calculation trace, audit trail). These are not interchangeable.",
    project:   "Coop has two separate documents: Finance Brief (capex, savings, payback, financing signal) and Climate Credentials Report (DPCS-H v1.0 breakdown, benchmark, audit trail, QR verification). Know which you are sending before any meeting.",
    practical: "Before any meeting: download both reports from the Reports page. Note which section concerns a bank (Finance Brief: capex, signal, risk) versus a verifier (CCR: methodology, evidence links, calculation hash). Tailor your conversation.",
    checklist: ["Know the exact purpose difference between Finance Brief and CCR", "Understand what a VVB (Validation/Verification Body) does", "Explain the calculation hash and why it matters for a verifier"],
    red_flag:  "Red flag: Never send a Climate Credentials Report to a bank as a financing proposal substitute. Methodology documentation without financial context is not an investment case.",
  },
];


export default function HousingTraining() {
  const navigate = useNavigate();
  const STORAGE_KEY = "housing_training_progress";

  const [progress, setProgress] = useState(() => {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {}; }
    catch { return {}; }
  });
  const [activeModule, setActiveModule] = useState(null);
  const [downloading,  setDownloading]  = useState(false);

  const completeModule = (moduleId) => {
    const newProgress = { ...progress, [moduleId]: true };
    setProgress(newProgress);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newProgress));
    toast.success("Module completed!");
  };

  const completedCount = MODULES.filter(m => progress[m.id]).length;
  const allComplete    = completedCount === MODULES.length;

  const downloadCertificate = async () => {
    if (!allComplete) { toast.error("Complete all 5 modules to unlock your certificate."); return; }
    setDownloading(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token   = localStorage.getItem("decarboniq_token");
      const res = await fetch(`${API_URL}/api/housing/training-certificate`,
        { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "DeCarbonIQ-HousingCarbon-Certificate.pdf";
      a.click(); URL.revokeObjectURL(a.href);
      toast.success("Certificate downloaded!");
    } catch { toast.error("Certificate download failed. Please try again."); }
    setDownloading(false);
  };

  return (
    <div className="p-6 lg:p-8 min-h-screen">
        <div className="mb-6">
          
      <PageHeader
        label="Housing Project"
        title="Climate Training"
      />
          <p className="text-xs text-slate-400 mt-0.5">
            5 short housing-specific modules · Complete all to unlock your certificate
          </p>
        </div>

        {/* Progress bar */}
        <div className="bg-white border border-slate-200 rounded-xl p-5 mb-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-bold text-slate-900">{completedCount}/{MODULES.length} completed</p>
            {allComplete ? (
              <span className="text-xs font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full flex items-center gap-0.5">
                <Award size={9} /> Certificate unlocked
              </span>
            ) : (
              <span className="text-xs text-slate-400">{MODULES.length - completedCount} remaining</span>
            )}
          </div>
          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full bg-teal-500 rounded-full transition-all duration-700"
              style={{ width: `${(completedCount / MODULES.length) * 100}%` }} />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Module list */}
          <div className="lg:col-span-1 space-y-2">
            {MODULES.map(m => {
              const done = progress[m.id];
              return (
                <button key={m.id}
                  onClick={() => setActiveModule(activeModule?.id === m.id ? null : m)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-colors border ${
                    activeModule?.id === m.id ? "bg-[#0F172A] border-[#0F172A]" :
                    done ? "bg-emerald-50 border-emerald-200" : "bg-white border-slate-200 hover:border-slate-300"
                  }`}
                  data-testid={`module-${m.id}`}>
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold ${
                    done ? "bg-emerald-500 text-white" :
                    activeModule?.id === m.id ? "bg-teal-500 text-white" : "bg-slate-100 text-slate-500"
                  }`}>
                    {done ? <CheckCircle2 size={14} /> : m.order}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-xs font-bold truncate ${activeModule?.id === m.id ? "text-white" : "text-slate-900"}`}>{m.title}</p>
                    <p className={`text-xs ${activeModule?.id === m.id ? "text-slate-400" : "text-slate-400"}`}>{m.duration}</p>
                  </div>
                  <ChevronRight size={12} className={activeModule?.id === m.id ? "text-slate-400" : "text-slate-300"} />
                </button>
              );
            })}

            {/* Certificate */}
            <div className={`mt-4 rounded-xl border-2 p-4 ${allComplete ? "border-emerald-300 bg-emerald-50" : "border-slate-200 bg-white"}`}>
              <div className="flex items-center gap-2 mb-2">
                <Award size={16} className={allComplete ? "text-emerald-600" : "text-slate-300"} />
                <p className={`text-sm font-bold ${allComplete ? "text-emerald-800" : "text-slate-400"}`}>
                  {allComplete ? "Certificate Ready" : "Complete all modules"}
                </p>
              </div>
              <p className="text-xs text-slate-500 mb-3">
                {allComplete ? "Low-Carbon Housing Programme · DeCarbonIQ" : `${MODULES.length - completedCount} modules remaining`}
              </p>
              <button onClick={downloadCertificate} disabled={!allComplete || downloading}
                className="w-full flex items-center justify-center gap-2 py-2 bg-emerald-600 text-white text-xs font-bold rounded-lg disabled:opacity-40 hover:bg-emerald-700 transition-colors"
                data-testid="download-certificate">
                {downloading ? <><RefreshCw size={12} className="animate-spin" /> Generating...</> : <><Download size={12} /> Download Certificate</>}
              </button>
            </div>
          </div>

          {/* Module content */}
          <div className="lg:col-span-2">
            {activeModule ? (
              <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="module-content">
                <div className="bg-[#0F172A] px-6 py-5">
                  <p className="text-xs text-slate-400 uppercase tracking-wide mb-1">Module {activeModule.order} of {MODULES.length} · {activeModule.duration}</p>
                  <h2 className="text-lg font-bold text-white">{activeModule.title}</h2>
                </div>
                <div className="p-6 space-y-5" data-testid="module-content">
                  <div>
                    <p className="text-xs text-slate-400 uppercase tracking-wide font-bold mb-1">Why This Matters</p>
                    <p className="text-sm text-slate-700 leading-relaxed">{activeModule.why}</p>
                  </div>
                  <div className="bg-teal-50 border border-teal-200 rounded-xl px-4 py-3">
                    <p className="text-xs text-teal-600 font-bold uppercase tracking-wide mb-1">Key Concept</p>
                    <p className="text-sm text-teal-800 font-medium leading-relaxed">{activeModule.concept}</p>
                  </div>
                  <div className="bg-slate-900 rounded-xl px-4 py-3">
                    <p className="text-xs text-slate-400 font-bold uppercase tracking-wide mb-1">What This Means for This Project</p>
                    <p className="text-xs text-slate-300 leading-relaxed">{activeModule.project}</p>
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3">
                    <p className="text-xs text-blue-600 font-bold uppercase tracking-wide mb-1">Practical Application</p>
                    <p className="text-sm text-blue-800 leading-relaxed">{activeModule.practical}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 font-bold uppercase tracking-wide mb-2">Mini Checklist</p>
                    <div className="space-y-1.5">
                      {activeModule.checklist.map((item, i) => (
                        <div key={i} className="flex items-start gap-2 text-xs text-slate-600">
                          <CheckCircle2 size={12} className="text-teal-500 flex-shrink-0 mt-0.5" /> {item}
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                    <p className="text-xs text-red-600 font-bold uppercase tracking-wide mb-1">Common Mistake</p>
                    <p className="text-xs text-red-700 leading-relaxed">{activeModule.red_flag}</p>
                  </div>
                  {!progress[activeModule.id] ? (
                    <button onClick={() => completeModule(activeModule.id)}
                      className="w-full py-3 bg-[#0F172A] text-white text-sm font-bold rounded-xl hover:bg-slate-800"
                      data-testid="complete-module">
                      Mark as Complete
                    </button>
                  ) : (
                    <div className="flex items-center gap-2 py-3 px-4 bg-emerald-50 border border-emerald-200 rounded-xl">
                      <CheckCircle2 size={16} className="text-emerald-600" />
                      <p className="text-sm font-bold text-emerald-800">Module Complete</p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-white border border-slate-200 rounded-xl p-10 text-center">
                <PlayCircle size={32} className="mx-auto mb-3 text-slate-200" />
                <p className="text-sm font-semibold text-slate-500">Select a module to start</p>
                <p className="text-xs text-slate-400 mt-1">Each module takes 5–8 minutes</p>
              </div>
            )}
          </div>
        </div>
    </div>
  );
}
